import React from "react";
import ReactDOM from "react-dom/client";
import { SplashScreen } from "@capacitor/splash-screen";

import { applyDeviceLanguage, initI18n, syncDayjsLocale } from "@/core/i18n";
import type { BCP47LangCode } from "@/core/i18n";
import { initDatadog } from "@/core/monitoring/datadog";
import { AppRouter } from "modules/routing/appRouter";
import { initStorage, migrateLocalStorageToPreferences } from "modules/storage";
import { FFlagProvider } from "services/Flags";
import "./index.css";
import { initializeDayjsPlugins } from "utils/dayjsUtils";
import { isNativeMobileApp } from "utils/platform.helpers";

initDatadog();

initializeDayjsPlugins();

// Initialize app with storage migration
async function init() {
  // Migrate localStorage to Capacitor Preferences (native only, runs once)
  await migrateLocalStorageToPreferences();

  // Load all storage keys into memory cache for sync access
  await initStorage();

  // On first launch, seed the language from the device/OS locale so the app
  // opens in the user's language (no-op once a language has been chosen).
  await applyDeviceLanguage();

  // Initialize i18n and preload the resolved locale's dictionary before the
  // first render so translated copy is available immediately (no key flash).
  const i18n = await initI18n();

  // Align dayjs (relative time / localized dates) with the resolved language.
  await syncDayjsLocale((i18n.resolvedLanguage ?? "en-US") as BCP47LangCode);

  const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

  root.render(
    <React.StrictMode>
      <FFlagProvider>
        <AppRouter />
      </FFlagProvider>
    </React.StrictMode>,
  );

  if (isNativeMobileApp()) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => void SplashScreen.hide());
    });
  }
}

init();
